import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useAuthStore } from '../../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import Label from '../../UI/Label/Label';
import type { IntegrationProviderConfig } from '../types';
import DynamicFieldRenderer from './DynamicFieldRenderer';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';

interface PushModePanelProps {
  config: IntegrationProviderConfig;
  formValues: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: () => void;
  nodeId: string;
  provider: string;
  disabled?: boolean;
  connected?: boolean;
  validatorPreview?: string;
  validatorWarnings?: string[];
  nodeData?: Record<string, unknown>;
  errorFieldName?: string | null;
}

function parseCustomHeaders(raw: unknown): Record<string, string> {
  if (raw == null || typeof raw !== 'string') return {};
  const out: Record<string, string> = {};
  raw
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .forEach(line => {
      const idx = line.indexOf(':');
      if (idx > 0) {
        const key = line.slice(0, idx).trim();
        const value = line.slice(idx + 1).trim();
        if (key) out[key] = value;
      }
    });
  return out;
}

const PushModePanel: React.FC<PushModePanelProps> = ({
  config,
  formValues,
  onChange,
  onFieldFocus,
  onFieldBlur,
  nodeId,
  provider,
  disabled,
  connected,
  validatorPreview,
  validatorWarnings,
  nodeData,
  errorFieldName,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(s => s.setNotification);
  const [testPushLoading, setTestPushLoading] = useState(false);

  const pushConfig = config.pushConfig;
  if (!pushConfig) return null;

  const selectedAction = (formValues.pushAction as string) ?? pushConfig.actions[0]?.value;
  const fields = pushConfig.getFields(selectedAction);

  const webhookUrl = (formValues.webhookUrl as string) ?? '';
  const webhookValidation = pushConfig.validateWebhookUrl?.(webhookUrl);
  const webhookUrlError =
    webhookValidation &&
    webhookUrl.trim() !== '' &&
    !webhookValidation.valid;
  const effectiveErrorFieldName = webhookUrlError ? 'webhookUrl' : errorFieldName;

  const handleTestPush = async () => {
    if (pushConfig.validateWebhookUrl && webhookUrl.trim() !== '') {
      const validation = pushConfig.validateWebhookUrl(webhookUrl.trim());
      if (!validation.valid) {
        setNotification({
          type: NotificationType.Warning,
          message: validation.errorMessage ?? 'Invalid webhook URL',
        });
        return;
      }
    }
    const url = (formValues.webhookUrl as string)?.trim();
    if (!url) {
      setNotification({
        type: NotificationType.Warning,
        message: 'Enter the Webhook URL first.',
      });
      return;
    }
    setTestPushLoading(true);
    try {
      const customHeaders = parseCustomHeaders(formValues.customHeaders);
      const payloadValue =
        (formValues.payload as string)?.trim() ||
        '{"test":true,"message":"FluxPrompt test push"}';
      const payload: Record<string, unknown> = {
        pushAction: selectedAction,
        webhookUrl: url,
        payloadFormat: (formValues.payloadFormat as string) ?? 'json',
        payload: payloadValue,
        customHeaders: Object.keys(customHeaders).length > 0 ? customHeaders : undefined,
      };
      const integrationId = nodeData?.integrationId as string | undefined;
      if (integrationId) payload.integrationId = integrationId;
      const connectedByUserId = nodeData?.connectedByUserId as string | undefined;
      if (connectedByUserId) payload.connectedByUserId = connectedByUserId;

      const res = await ThirdPartyIntegrationService.testPush<{
        success?: boolean;
        status?: number;
        response?: unknown;
      }>(provider, payload);
      const data = res?.data;
      const success = data?.success === true;
      setNotification({
        type: success ? NotificationType.Success : NotificationType.Error,
        message: success
          ? `Test push succeeded (${data?.status ?? 200})`
          : `Test push failed: ${data?.status ?? 'error'} — ${JSON.stringify(data?.response ?? data ?? '')}`,
      });
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Test push failed';
      setNotification({ type: NotificationType.Error, message });
    } finally {
      setTestPushLoading(false);
    }
  };

  const isDark = user?.theme === 'dark';
  const showTestPush = !!pushConfig.supportsTestPush;
  const PushPanelExtras = pushConfig.PushPanelExtras;

  const selectSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiList-root': {
      backgroundColor: isDark ? '#101828' : '#F9FAFB',
    },
    borderRadius: '8px',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: isDark ? '#101828' : '#F9FAFB',
    color: isDark ? '#D0D5DD' : '#101828',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Label
          labelName="Action"
          isAdvanced
          labelStyle={{
            fontSize: '14px',
            color: isDark ? '#D0D5DD' : '#101828',
            fontWeight: 400,
          }}
        />
        <Select
          className="nodrag"
          value={selectedAction}
          onChange={e => onChange('pushAction', e.target.value)}
          onFocus={() => onFieldFocus?.('pushAction')}
          onBlur={onFieldBlur}
          disabled={disabled}
          sx={selectSx}
          size="small"
        >
          {pushConfig.actions.map(opt => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{ color: isDark ? '#D0D5DD' : 'black' }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <DynamicFieldRenderer
        fields={fields}
        values={formValues}
        onChange={onChange}
        onFieldFocus={onFieldFocus}
        onFieldBlur={onFieldBlur}
        nodeId={nodeId}
        provider={provider}
        context={{ action: selectedAction }}
        disabled={disabled}
        connected={connected}
        nodeData={nodeData}
        errorFieldName={effectiveErrorFieldName}
      />

      {webhookUrlError && webhookValidation?.errorMessage && (
        <Typography variant="caption" color="error" sx={{ mt: -1 }}>
          {webhookValidation.errorMessage}
        </Typography>
      )}

      {PushPanelExtras && (
        <PushPanelExtras
          provider={provider}
          config={config}
          formValues={formValues}
          selectedAction={selectedAction}
          nodeData={nodeData}
          disabled={disabled}
          isDark={isDark}
          setNotification={setNotification}
          onChange={onChange}
        />
      )}

      {showTestPush && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {!PushPanelExtras && (
            <Typography variant="caption" sx={{ color: isDark ? '#98A2B3' : '#667085' }}>
              Use Test Push to send a sample payload and verify the connection.
            </Typography>
          )}
          <Button
            className="nodrag"
            size="small"
            variant="outlined"
            onClick={handleTestPush}
            disabled={disabled || testPushLoading}
          >
            {testPushLoading ? 'Sending...' : 'Test Push'}
          </Button>
        </Box>
      )}

      {(validatorPreview !== undefined || (validatorWarnings?.length ?? 0) > 0) && (
        <Box>
          <Label
            labelName="Validator preview"
            isAdvanced
            labelStyle={{
              fontSize: '12px',
              color: isDark ? '#98A2B3' : '#667085',
            }}
          />
          {validatorWarnings?.map((w, i) => (
            <Box
              key={i}
              sx={{
                fontSize: '12px',
                color: '#F59E0B',
                mb: 0.5,
              }}
            >
              {w}
            </Box>
          ))}
          {validatorPreview && (
            <Box
              sx={{
                maxHeight: 100,
                overflow: 'auto',
                p: 1,
                borderRadius: 1,
                bgcolor: isDark ? '#1D2939' : '#F9FAFB',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {validatorPreview}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PushModePanel;
